<template>
  <div
    :class="['timeline', sizeClass]"
    :data-finished="isAllFinished"
    :data-canceled="isCanceled"
  >
    <slot>
      <timeline-item
        v-for="(step, i) in steps"
        :size="size"
        :key="step.title"
        :title="step.title"
        :data-finished="isFinished(i)"
      >
        <template #icon>
          <component
            :is="stepIcon(i)"
            class="size-[var(--icon-size)] rounded-full"
            :class="{
              'text-brand-600': isFinished(i),
              'text-gray-300': isFuture(i) || (!isCanceled && isCurrent(i)),
              'text-error-600': isCanceled && isCurrent(i),
              'text-success-600': isAllFinished,
              'shadow-[var(--box-shadow-focus)]': !isCanceled && isCurrent(i) && !isAllFinished,
            }"
          />
        </template>
        <template
          #content-inner
          v-if="isFinished(i) || isCurrent(i)"
        >
          <p class="timeline-subtitle">{{ step.subtitle }}</p>
          <p
            class="timeline-description"
            v-if="isCurrent(i) && (step.description || $slots.description)"
          >
            <slot name="description">
              <p>{{ step.description }}</p>
            </slot>
          </p>
        </template>
      </timeline-item>
    </slot>
  </div>
</template>

<script setup>
import { computed, provide } from 'vue';
import TimelineItem from './TimelineItem.vue';
import TimelineCheckedIcon from './TimelineCheckedIcon.vue';
import TimelineCurrentIcon from './TimelineCurrentIcon.vue';
import TimelineUncheckedIcon from './TimelineUncheckedIcon.vue';
import TimelineCanceledIcon from './TimelineCanceledIcon.vue';

defineOptions({
  name: 'AppTimeline',
});

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['lg', 'md', 'sm'].includes(value),
  },
  currentStep: {
    type: [String, Number],
    default: null,
  },
  canceledStep: {
    type: [String, Number],
    default: null,
  },
  steps: {
    type: Array,
    default: () => [
      {
        title: 'Step 1',
        subtitle: 'Subtitle 1',
        description: 'Description 1',
        value: null, // string, number, array
      },
    ],
  },
  direction: {
    type: String,
    default: 'vertical',
    validator: (value) => ['vertical', 'horizontal'].includes(value),
  },
  finalStep: {
    type: [String, Number],
    default: null,
  },
});

const currentIdx = computed(() => {
  return props.steps.findIndex((step) => {
    if (Array.isArray(step.value)) {
      return step.value.includes(props.currentStep);
    }
    return step.value === props.currentStep;
  });
});

const isFinished = (idx) => {
  return idx < currentIdx.value;
};

const isCurrent = (idx) => {
  return idx === currentIdx.value;
};

const isFuture = (idx) => {
  return idx > currentIdx.value;
};

const isCanceled = computed(() => {
  if (props.canceledStep === null) return false;
  return props.currentStep === props.canceledStep;
});

const isAllFinished = computed(() => {
  if (props.finalStep) {
    return props.finalStep === props.currentStep;
  }
  return currentIdx.value === props.steps.length - 1;
});

const stepIcon = (idx) => {
  if (isAllFinished.value) {
    return TimelineCheckedIcon;
  } else if (isFinished(idx)) {
    return TimelineCheckedIcon;
  } else if (isCanceled.value) {
    return TimelineCanceledIcon;
  } else if (isCurrent(idx)) {
    return TimelineCurrentIcon;
  } else {
    return TimelineUncheckedIcon;
  }
};

const sizeClass = computed(() => {
  return {
    lg: 'timeline-lg',
    md: 'timeline-md',
    sm: 'timeline-sm',
  }[props.size];
});

provide('timeline', {
  size: props.size,
});
</script>

<style>
@reference '@/assets/main.css';

.timeline {
  --icon-size: calc(var(--spacing) * 8);
  --connector-offset: calc(calc(var(--icon-size) / 2) - 1px);
  @apply flex flex-col text-base;

  &[data-finished='true'] {
    & .timeline-item {
      &[data-finished='true'] {
        &:after {
          @apply bg-success-600;
        }
      }
    }
  }

  &[data-canceled='true'] {
    & .timeline-item {
      @apply not-last:opacity-33;
    }
  }

  &.timeline-sm {
    --icon-size: calc(var(--spacing) * 6);
    @apply text-sm;
  }

  &.timeline-lg {
    --icon-size: calc(var(--spacing) * 10);
    @apply text-lg;
  }
}
</style>
